import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useBrandConfig } from "src/brand/config";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { HomeCard } from "src/core/components/home/HomeCard";
import { SubHeader } from "src/core/components/home/SubHeader";
import { CustomFolderFileType, Status } from "src/core/constants/folders";
import { CONTACT_MAIL } from "src/core/constants/general";
import { useFolderFilesCountQuery } from "src/core/gql/graphql";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { useStatusFilter } from "src/core/hooks/useStatusFilter";
import { withRequiredOverride } from "src/core/overrides";
import { CardData } from "src/core/types/folders";

const ClientCreationModal = withRequiredOverride("components.folder.ClientCreationModal");

export const Home = () => {
  const { t } = useTranslation();
  const paths = useRequiredTenantPaths();

  const [open, setOpen] = useState<boolean>(false);
  const { hasAccessToStatus } = useStatusFilter();
  const brandConfig = useBrandConfig();

  const {
    data: folderData,
    loading: foldersLoading,
    error: foldersError,
  } = useFolderFilesCountQuery({
    variables: {
      draftState: [Status.DRAFT],
      validationPendingState: [Status.VALIDATION_PENDING],
      validationPendingHrState: [Status.VALIDATION_PENDING_HR],
      missionCreation: [
        CustomFolderFileType.MISSION_CREATION,
        CustomFolderFileType.MISSION_CREATION_PPH,
        CustomFolderFileType.MISSION_RENEWAL,
        CustomFolderFileType.MISSION_RENEWAL_PPH,
      ],
      validatedState: [Status.VALIDATED],
    },
  });

  const number = {
    [Status.DRAFT]: folderData?.draft.pageInfo.totalCount || 0,
    [Status.VALIDATION_PENDING]: folderData?.validationPending.pageInfo.totalCount || 0,
    [Status.VALIDATION_PENDING_HR]: folderData?.validationPendingHr.pageInfo.totalCount || 0,
    [Status.RENEWAL]: folderData?.renewal.pageInfo.totalCount || 0,
    [Status.VALIDATED]: folderData?.validated.pageInfo.totalCount || 0,
    [Status.REJECTED]: 0,
  };

  if (foldersError) return <div>Erreur: {foldersError.message}</div>;

  const allCards: CardData[] = brandConfig.foldersConfig
    .filter((card) => hasAccessToStatus(card.status))
    .map((card) => ({
      ...card,
      title: t(card.titleKey),
      subtitle: t(card.subtitleKey),
      label: (() => {
        switch (card.status) {
          case Status.DRAFT:
            return t("home.toComplete");
          case Status.RENEWAL:
            return t("home.toRenew");
          case Status.VALIDATED:
            return t("home.treats");
          default:
            return t("home.toValidate");
        }
      })(),
      folderLabel:
        card.status === Status.VALIDATION_PENDING_HR
          ? t("home.riskClientFolders")
          : card.status === Status.RENEWAL
          ? t("home.renewalFoldersLabel")
          : t("home.clientFolders"),
      number: number[card.status],
    }));

  if (foldersLoading)
    return (
      <div className="home-container">
        <div className="home-container-loading">
          <Spinner size="2xl" />
        </div>
      </div>
    );

  return (
    <div className="home-container">
      <SubHeader onAddClick={() => setOpen(true)} />
      <div className="home-outlet-container">
        <div className="home-card-container">
          {allCards.map((item, index: number) => (
            <Link
              key={index}
              to={paths.folders()}
              state={{ status: item.status }}
              className="no-underline text-inherit"
            >
              <HomeCard
                isLoading={foldersLoading}
                icon={item.icon}
                title={item.title}
                subtitle={item.subtitle}
                number={item.number}
                label={item.label}
                folderLabel={item.folderLabel}
                variant={item.variant}
              />
            </Link>
          ))}
        </div>
      </div>
      <ClientCreationModal open={open} setOpen={setOpen} />
      <div className="home-container-footer">
        <div className="home-container-footer-content">
          <div className="font-medium">{t("home.footerQuestion")}</div>
          <div className="font-light">
            {t("home.footerContact")}
            <a href={`mailto:${CONTACT_MAIL}`}>{CONTACT_MAIL}</a>
          </div>
        </div>
      </div>
    </div>
  );
};
